import { ExtractCFromProps } from 'mobx-state-tree/dist/internal';
import { IModelType, Instance, ModelCreationType, ModelProperties, SnapshotIn, cast, flow, getRoot, types } from 'mobx-state-tree';
import { clientFunction } from '../../../config/apollo/client';
import { filterDefinedFields } from '../../../utils/filterFields';

const FetchingState = types.model('FetchingState', {
  id: types.string,
  updatedAt: types.maybe(types.number),
}).volatile(self => ({
  // use volatile to avoid persistence of transient state
  // NOTE: volatile state can only be modified using actions, and cannot be initialized in create() or by assignment!
  // NOTE: volatile state is only shallowly observable (primitives only)
  state: 'idle' as 'idle' | 'pending' | 'done' | 'error',
  error: undefined as string | null | undefined,
  data: undefined as unknown | null | undefined,
}))
  .actions(self => ({
    setIdle() {
      self.state = 'idle';
      self.error = null;
      self.data = null;
    },
    setPending() {
      self.state = 'pending';
      self.error = null;
      self.data = null;
    },
    setDone(data: unknown) {
      self.state = 'done';
      self.error = null;
      self.data = data;
    },
    setError(error: string) {
      self.state = 'error';
      self.error = error;
      self.data = null;
    },
  }))
  .views(self => ({
    isIdle() {
      return self.state === 'idle';
    },
    isPending() {
      return self.state === 'pending';
    },
    isDone() {
      return self.state === 'done' && self.data !== undefined;
    },
    hasError() {
      return self.state === 'error';
    },
    isStale(thresholdMs: number) {
      if (self.updatedAt === undefined) {
        return true;
      }
      return self.updatedAt + thresholdMs < Date.now();
    }
  }));

export interface IFetchingState extends Instance<typeof FetchingState> {}

export const Fetcher = types
  .model('Fetcher', {
    states: types.array(FetchingState),
  })
  .volatile(self => ({
    staleTime: 10 * 60 * 1000, // default 10 minutes
  }))
  .actions(self => {
    // a mechanism to handle simultaneous fetches for the same id
    const map = new Map<string, Promise<unknown>>();
    return {
      /**
       * Deduplicate async operations by id. Useful for avoiding multiple simultaneous fetches for the same resource.
       */
      _deduplicate(id: string, fetcher: () => Promise<unknown>): Promise<unknown> {
        if (map.has(id)) {
          return map.get(id)!;
        }
        const promise = fetcher().finally(() => {
          map.delete(id);
        });
        map.set(id, promise);
        return promise;
      }
    };
  })
  .views(self => ({
    isFetchDone(id: string) {
      return self.states.find(state => state.id === id)?.isDone() || false;
    },
    hasFetchError(id: string) {
      return self.states.find(state => state.id === id)?.hasError() || false;
    },
    isFetchPending(id: string) {
      return self.states.find(state => state.id === id)?.isPending() || false;
    },
    isFetchIdle(id: string) {
      return !self.states.some(state => state.id === id) || self.states.find(state => state.id === id)?.isIdle() || false;
    },
    isFetchStale(id: string) {
      const state = self.states.find(state => state.id === id);
      if (!state) {
        return true; // not fetched yet, consider stale
      }
      return state.isStale(self.staleTime);
    },
  }))
  .actions(self => ({
    initializeFetchState(id: string) {
      const index = self.states.findIndex(p => p.id === id);
      if (index === -1) {
        const state = FetchingState.create({ id });
        return self.states.push(state) - 1;
      }
      return index;
    }
  }))
  .actions(self => ({
    clearFetchStates() {
      self.states = cast([]);
    },
    clearFetchState(id: string) {
      self.states = cast(self.states.filter(p => p.id !== id));
    },
    setFetchDone(id: string, data: any) {
      const index = self.initializeFetchState(id);
      self.states[index].setDone(data);
    },
    setFetchError(id: string, error: string) {
      const index = self.initializeFetchState(id);
      self.states[index].setError(error);
      console.log(`Fetcher.setFetchError(${id}, ${error})`, self.states.find(s => s.id === id));
    },
    setFetchPending(id: string) {
      const index = self.initializeFetchState(id);
      self.states[index].setPending();
    },
    setFetchIdle(id: string) {
      const index = self.initializeFetchState(id);
      self.states[index].setIdle();
    },
  }))
  .actions(self => ({
    loadFetchedData<P extends ModelProperties, O>(model: IModelType<P, O>, id: string, cb: (partial: Partial<ModelCreationType<ExtractCFromProps<P>>>) => void | Promise<void>) {
      const index = self.states.findIndex(p => p.id === id);
      if (index === -1) {
        throw new Error(`State with id ${id} not found.`);
      }
      const state = self.states[index];
      if (!self.isFetchDone(id)) {
        throw new Error(`State with id ${id} is not done yet. Current state: ${state.state}`);
      }
      if (typeof state.data !== 'object' && state.data !== null) {
        throw new Error(`Expected state with id ${id} to contain object data, but got ${typeof state.data}`);
      }
      const partial = {
        ...filterDefinedFields(state.data as SnapshotIn<IModelType<P, O>>, model.properties)
      };
      try {
        // consume data, set fetch state to idle
        cb(partial);
        self.states[index].updatedAt = Date.now();
        self.setFetchIdle(id);
      } catch (error) {
        console.error(error);
        if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
          self.setFetchError(id, error.message);
        } else {
          self.setFetchError(id, JSON.stringify(error));
        }
      }
    }
  }))
  .actions(self => ({
    fetchData: flow(function* (id: string, fetcher: () => Promise<unknown>) {
      self.setFetchPending(id);
      try {
        const data = yield fetcher();
        self.setFetchDone(id, data);
        return data;
      } catch (error) {
        console.error(`Error fetching data for id ${id}:`, error);
        const errorMessage =
          error !== null &&
          typeof error === 'object' &&
          'message' in error &&
          typeof error.message === 'string'
            ? error.message
            : JSON.stringify(error);
        self.setFetchError(id, errorMessage);
      }
    }),
  }))
  .views(self => ({
    get client() {
      const lang = getRoot<any>(self)?.language?.name?.key || 'en';
      return clientFunction(lang);
    },
    get language() {
      return getRoot<any>(self)?.language?.name?.key || 'en';
    },
    getFetchError(id: string) {
      return self.states.find(state => state.id === id)?.error;
    },
    getFetchedData(id: string) {
      return self.states.find(state => state.id === id)?.data;
    },
    shouldRefetch(id: string) {
      if (self.isFetchPending(id)) {
        return false;
      }
      if (self.isFetchStale(id)) {
        console.log('Fetcher.shouldRefetch: stale', id);
        return true;
      }
      return false;
    },
  }));
